// src/contexts/MallContext.tsx
//
// 選択中のモール（楽天 / Amazon）を管理するコンテキスト。
// localStorage に永続化し、リロード後も選択が維持される。

import React, { createContext, useCallback, useContext, useState } from "react";
import type { Mall, ShopRef } from "../types/mall";

const LS_KEY_MALL = "selectedMall";
const LS_KEY_SHOP_ID = "selectedShopId";

export type MallContextValue = {
    /** 現在選択中のモール */
    mall: Mall;
    setMall: (m: Mall) => void;

    /** 現在選択中の店舗（モールをまたぐ識別子） */
    selectedShop: ShopRef | null;
    setSelectedShop: (shop: ShopRef | null) => void;

    /** 全連携済み店舗（フロントで保持するキャッシュ） */
    shops: ShopRef[];
    setShops: (shops: ShopRef[]) => void;

    /** 管理ツールの「利用モール」で有効化されたモール（サイドバーの連携設定メニュー表示ゲート） */
    enabledMalls: string[];
    setEnabledMalls: (malls: string[]) => void;

    /** 楽天ショップが連携済みか */
    hasRakuten: boolean;
    /** Amazonショップが連携済みか */
    hasAmazon: boolean;
    /** Yahooショップが連携済みか */
    hasYahoo: boolean;
};

const MallContext = createContext<MallContextValue>({
    mall: "rakuten",
    setMall: () => {},
    selectedShop: null,
    setSelectedShop: () => {},
    shops: [],
    setShops: () => {},
    enabledMalls: [],
    setEnabledMalls: () => {},
    hasRakuten: false,
    hasAmazon: false,
    hasYahoo: false,
});

function readStoredMall(): Mall {
    if (typeof window === "undefined") return "rakuten";
    const v = localStorage.getItem(LS_KEY_MALL);
    if (v === "amazon" || v === "rakuten" || v === "yahoo") return v;
    return "rakuten";
}

export function MallProvider({ children }: { children: React.ReactNode }) {
    const [mall, setMallState] = useState<Mall>(readStoredMall);
    const [selectedShop, setSelectedShopState] = useState<ShopRef | null>(null);
    const [shops, setShopsState] = useState<ShopRef[]>([]);
    const [enabledMalls, setEnabledMalls] = useState<string[]>([]);

    const setMall = useCallback((m: Mall) => {
        if (m === mall) return;
        localStorage.setItem(LS_KEY_MALL, m);
        setMallState(m);
        const nextShop = shops.find((shop) => shop.mall === m) ?? null;
        if (nextShop) {
            localStorage.setItem(LS_KEY_SHOP_ID, nextShop.shopId);
            setSelectedShopState(nextShop);
        }

        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("mall-selection-changed", { detail: { mall: m } }));
        }
    }, [mall, shops]);

    const setSelectedShop = useCallback((shop: ShopRef | null) => {
        if (shop) {
            localStorage.setItem(LS_KEY_SHOP_ID, shop.shopId);
        } else {
            localStorage.removeItem(LS_KEY_SHOP_ID);
        }
        setSelectedShopState(shop);
    }, []);

    const setShops = useCallback((next: ShopRef[]) => {
        setShopsState(next);
        // selectedShop が shops になければリセット
        const storedShopId = localStorage.getItem(LS_KEY_SHOP_ID);
        if (storedShopId) {
            const found = next.find((s) => s.shopId === storedShopId);
            if (found) {
                setSelectedShopState(found);
                return;
            }
        } else {
            const byMall = next.find((s) => s.mall === mall);
            const fallback = byMall ?? (next[0] ?? null);
            if (fallback) {
                localStorage.setItem(LS_KEY_SHOP_ID, fallback.shopId);
            } else {
                localStorage.removeItem(LS_KEY_SHOP_ID);
            }
            setSelectedShopState(fallback);
            return;
        }
        const byMall = next.find((s) => s.mall === mall);
        const fallback = byMall ?? (next[0] ?? null);
        if (fallback) {
            localStorage.setItem(LS_KEY_SHOP_ID, fallback.shopId);
        } else {
            localStorage.removeItem(LS_KEY_SHOP_ID);
        }
        setSelectedShopState(fallback);
    }, [mall]);

    const hasRakuten = shops.some((s) => s.mall === "rakuten");
    const hasAmazon = shops.some((s) => s.mall === "amazon");
    const hasYahoo = shops.some((s) => s.mall === "yahoo");

    return (
        <MallContext.Provider
            value={{ mall, setMall, selectedShop, setSelectedShop, shops, setShops, enabledMalls, setEnabledMalls, hasRakuten, hasAmazon, hasYahoo }}
        >
            {children}
        </MallContext.Provider>
    );
}

export function useMallContext() {
    return useContext(MallContext);
}
